import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { fmtUSD, fmtDate } from '@/lib/calc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertCircle, RefreshCw, Search, Plus, FileText, Copy, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['Draft', 'InternalReview', 'Sent', 'Approved', 'Declined', 'Expired', 'Converted'];
const STATUS_LABEL = {
  Draft: 'Draft', InternalReview: 'Internal Review', Sent: 'Sent',
  Approved: 'Approved', Declined: 'Declined', Expired: 'Expired', Converted: 'Converted',
};
const STATUS_STYLE = {
  Draft: 'bg-[hsl(var(--muted))] text-muted-foreground border-border',
  InternalReview: 'bg-[hsl(60_30%_88%)] text-foreground border-[hsl(60_20%_78%)]',
  Sent: 'bg-[hsl(var(--soft-blush))] text-foreground border-[hsl(var(--soft-blush-dark))]',
  Approved: 'bg-[hsl(var(--muted-green))]/25 text-[hsl(var(--muted-green-dark))] border-[hsl(var(--muted-green))]/50',
  Declined: 'bg-foreground text-background border-foreground',
  Expired: 'bg-foreground/70 text-background border-foreground',
  Converted: 'bg-[hsl(var(--muted-green))] text-white border-[hsl(var(--muted-green-dark))]',
};

const PAGE = 15;

export default function Quotations() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const canCreate = ['admin','manager','sales'].includes(role);
  const showCosts = ['admin','manager','purchasing'].includes(role);

  const [q, setQ] = useState('');
  const [statuses, setStatuses] = useState(new Set());
  const [page, setPage] = useState(0);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE));
    p.set('offset', String(page * PAGE));
    if (q.trim()) p.set('search', q.trim());
    if (statuses.size) p.set('status', [...statuses].join(','));
    return p.toString();
  }, [q, statuses, page]);

  const load = useCallback(async () => {
    setState('loading'); setError(null);
    try {
      setData(await apiFetch(`/quotations?${params}`));
      setState('ok');
    } catch (e) { setError(e.message); setState('error'); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const toggle = (s) => setStatuses(prev => {
    const n = new Set(prev);
    n.has(s) ? n.delete(s) : n.add(s);
    setPage(0);
    return n;
  });

  const duplicate = async (id) => {
    try {
      const r = await apiFetch(`/quotations/${id}/duplicate`, { method: 'POST', body: '{}' });
      toast.success(`Duplicated as ${r.number}`);
      navigate(`/quotations/${r.id}`);
    } catch (e) { toast.error(e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1400px]" data-testid="quotations-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="velvet-eyebrow mb-3">Proposals</div>
          <h1 className="velvet-page-title">Quotations</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
            BOM-aware proposals. Every quotation freezes the exact recipe version and cost snapshot used at the time of quoting.
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => navigate('/quotations/new')}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="new-quotation-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New quotation
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={e => { setQ(e.target.value); setPage(0); }}
            placeholder="Search by number or customer…"
            className="pl-9 rounded-sm"
            data-testid="quotations-search"
          />
        </div>
        {STATUSES.map(s => (
          <button
            key={s}
            onClick={() => toggle(s)}
            className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 border rounded-sm transition-colors ${
              statuses.has(s)
                ? STATUS_STYLE[s]
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`filter-${s}`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {state === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center" data-testid="quotations-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading quotations…
        </div>
      )}
      {state === 'error' && (
        <div className="velvet-card p-8 text-center max-w-md mx-auto" data-testid="quotations-error">
          <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button onClick={load} className="rounded-sm" variant="outline" data-testid="quotations-retry">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </div>
      )}
      {state === 'ok' && (
        <>
          <div className="velvet-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-foreground/15 text-left">
                    <th className="px-5 py-3 velvet-eyebrow">Number</th>
                    <th className="px-5 py-3 velvet-eyebrow">Customer</th>
                    <th className="px-5 py-3 velvet-eyebrow hidden md:table-cell">Created</th>
                    <th className="px-5 py-3 velvet-eyebrow hidden lg:table-cell">Valid until</th>
                    <th className="px-5 py-3 velvet-eyebrow text-right">Total</th>
                    <th className="px-5 py-3 velvet-eyebrow">Status</th>
                    <th className="px-5 py-3 velvet-eyebrow text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(row => (
                    <tr key={row.id} className="border-b border-border hover:bg-[hsl(var(--muted))]/50 transition-colors" data-testid={`quotation-row-${row.number}`}>
                      <td className="px-5 py-4">
                        <Link to={`/quotations/${row.id}`} className="font-mono text-xs hover:underline underline-offset-4" data-testid={`quotation-open-${row.number}`}>
                          {row.number}
                        </Link>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-serif text-lg">{row.customer_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{row.type} · {row.fulfillment}</div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell text-muted-foreground">{fmtDate(row.created_at)}</td>
                      <td className="px-5 py-4 hidden lg:table-cell text-muted-foreground">{row.valid_until ? fmtDate(row.valid_until) : '—'}</td>
                      <td className="px-5 py-4 text-right velvet-money">
                        {row.total != null ? fmtUSD(row.total) : <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Pending</span>}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${STATUS_STYLE[row.status] || ''}`} data-testid={`quotation-status-${row.number}`}>
                          {STATUS_LABEL[row.status] || row.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex gap-1.5">
                          <Link to={`/quotations/${row.id}`}>
                            <Button size="sm" variant="outline" className="rounded-sm" data-testid={`open-quote-${row.number}`}>Open</Button>
                          </Link>
                          {canCreate && (
                            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => duplicate(row.id)} data-testid={`duplicate-quote-${row.number}`}>
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Link to={`/quotations/${row.id}/pdf`}>
                            <Button size="sm" variant="outline" className="rounded-sm" data-testid={`pdf-quote-${row.number}`}>
                              <FileText className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-16 text-center text-muted-foreground" data-testid="quotations-empty">
                        No quotations match your filters yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between mt-5 text-sm">
            <div className="text-muted-foreground" data-testid="quotations-total">
              {data.total} total · page {page + 1} of {totalPages}
            </div>
            <div className="inline-flex gap-1">
              <Button size="sm" variant="outline" className="rounded-sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="quotations-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="quotations-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
